import { QueryErrorMessages } from '../enum/cube-error-message.enum';
import {
  CubeTimeDimensionGranularity,
  Weekdays,
} from '../enum/cube-schema.enum';
import { TeamAnalyticsActivitySegments } from '../enum/team-analytics-activity/segments.enum';
import { ITeamAnalyticsActivityEnum } from '../enum/team-analytics-activity/team-analytics-activity.enum';
import { ICubeRepository } from '../interface/cube-repository.interface';
import { ITeamAnalyticsActivityRepository } from '../interface/team-analytics-activity-repository.interface';
import { Query } from '@cubejs-client/core';
import {
  mapAllTimeAverageTeamMetrics,
  mapLatestTeamMetrics,
  mapTeamAllTimeSeriesAppMetrics,
  mapTeamAverageTimeSeriesAppMetrics,
  mapTeamTimeSeriesAppMetrics,
  mapUserTimeSeriesAppMetrics,
  mapUserCount,
  mapAllTimeWeekdayAverageTeamAppMetrics,
  mapTeamTotalMetrics,
  mapLatestFilteredTeamAppMetricsItem,
  mapLatestFilteredTeamSubscriptionMetricsItem,
} from '../mappers/team-analytics-activity.mapper';
import { IAppEnum } from '../enum/app.enum';
import { QueryLatestTeamAppMetricsRequestParamsDto } from '../dto/team-app-metrics/request/query-latest-team-app-metrics-request.dto';
import { QueryAllTimeAverageTeamAppMetricsRequestParamsDto } from '../dto/team-app-metrics/request/query-all-time-average-team-app-metrics-request.dto';
import { QueryAllTimeWeekdayAverageTeamAppMetricsRequestParamsDto } from '../dto/team-app-metrics/request/query-all-time-weekday-average-team-app-metrics-request.dto';
import { QueryAllTimeMaxAppMetricRequestParamsDto } from '../dto/team-app-metrics/request/query-all-time-max-app-metric-request.dto';
import { LatestTeamAppMetricsResult } from '../dto/team-app-metrics/cube/mapped/latest-team-app-metrics-result.dto';
import { AllTimeAverageTeamAppMetricsResult } from '../dto/team-app-metrics/cube/mapped/all-time-average-team-app-metrics-result.dto';
import { AllTimeWeekdayAverageTeamAppMetricsResult } from '../dto/team-app-metrics/cube/mapped/all-time-weekday-average-team-app-metrics-result.dto';
import { CubeConfigDto } from '../dto/common/request/cube-config.dto';
import { FilteredTeamAppMetricsResult } from '../dto/team-app-metrics/cube/mapped/filtered-team-app-metrics-result.dto';
import { FilteredTeamSubscriptionMetricsResult } from '../dto/team-app-metrics/cube/mapped/filtered-team-subscriptions-result.dto';
import { QueryLatestFilteredTeamAppMetricsRequestParamsDto } from '../dto/team-app-metrics/request/query-latest-filtered-team-app-metrics-request.dto';
import { QueryLatestFilteredTeamSubscriptionMetricsRequestParamsDto } from '../dto/team-app-metrics/request/query-latest-filtered-team-subscription-metrics-request.dto';
import { QueryUserCountRequestParamsDto } from '../dto/team-app-metrics/request/query-user-count-request.dto';
import { QueryTeamTotalMetricsRequestParamsDto } from '../dto/team-app-metrics/request/query-team-total-metrics-request.dto';
import { TeamTotalMetricsResult } from '../dto/team-app-metrics/cube/mapped/team-total-metrics-result.dto';
import { UserCountResult } from '../dto/team-app-metrics/cube/mapped/user-count-result.dto';
import { QueryTeamUserTimeSeriesAppMetricsRequestParamsDto } from '../dto/top-apps/request/query-team-user-time-series-app-metrics-request.dto';
import {
  TeamTimeSeriesResult,
  TeamUserTimeSeriesResult,
} from '../dto/team-app-metrics/cube/mapped/team-time-series-result.dto';
import { TeamTimeSeriesAllTimeAverageResult } from '../dto/team-app-metrics/cube/mapped/team-time-series-all-time-average-result.dto';
import { QueryTeamAverageTimeSeriesAppMetricsRequestParamsDto } from '../dto/team-app-metrics/request/query-team-average-time-series-app-metrics-request.dto';
import { QueryTeamTimeSeriesAppMetricsRequestParamsDto } from '../dto/team-app-metrics/request/query-team-time-series-app-metrics-request.dto';
import { QueryTeamTimeSeriesAllTimeAverageAppMetricsRequestParamsDto } from '../dto/team-app-metrics/request/query-team-time-series-all-time-average-app-metrics-request.dto';

export class TeamAnalyticsActivityRepository
  implements ITeamAnalyticsActivityRepository
{
  cubeRepository: ICubeRepository;
  constructor(deps: { repository: ICubeRepository }) {
    this.cubeRepository = deps.repository;
  }

  async queryLatestTeamAppMetrics(
    params: QueryLatestTeamAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<LatestTeamAppMetricsResult> {
    try {
      const query: Query = {
        measures: [
          ITeamAnalyticsActivityEnum.TotalDuration,
          ITeamAnalyticsActivityEnum.Count,
          ITeamAnalyticsActivityEnum.TotalCallDuration,
          ITeamAnalyticsActivityEnum.CallCount,
          ITeamAnalyticsActivityEnum.DayCount,
          ITeamAnalyticsActivityEnum.UserCount,
        ],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityEnum.LocalStartTime,
            granularity:
              params.frequency === CubeTimeDimensionGranularity.Week
                ? CubeTimeDimensionGranularity.Week
                : CubeTimeDimensionGranularity.Day,
            dateRange:
              params.frequency === CubeTimeDimensionGranularity.Week
                ? [params.startDate, params.endDate]
                : [params.endDate, params.endDate],
          },
        ],
        filters: [
          {
            member: ITeamAnalyticsActivityEnum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityEnum.AppId,
            operator: 'equals',
            values: [params.appId],
          },
        ],
        order: [[ITeamAnalyticsActivityEnum.LocalStartTime, 'desc']],
      };

      if (params?.userIds?.length)
        query.filters.push({
          member: ITeamAnalyticsActivityEnum.UserId,
          operator: 'equals',
          values: params.userIds,
        });

      const latestTeamMetrics = await this.cubeRepository.query(
        config.token,
        query
      );

      return mapLatestTeamMetrics(latestTeamMetrics);
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryLatestTeamAppMetricsError}: ${e}`
      );
    }
  }

  async queryLatestFilteredTeamAppMetrics(
    params: QueryLatestFilteredTeamAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<Omit<FilteredTeamAppMetricsResult, 'userCount'>[]> {
    try {
      const query: Query = {
        measures: [
          ITeamAnalyticsActivityEnum.TotalDuration,
          ITeamAnalyticsActivityEnum.Count,
        ],
        dimensions: [
          ITeamAnalyticsActivityEnum.AppId,
          IAppEnum.Name,
          IAppEnum.LogoUrl,
          IAppEnum.DisplayColor,
        ],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityEnum.LocalStartTime,
            granularity:
              params.frequency === CubeTimeDimensionGranularity.Week
                ? CubeTimeDimensionGranularity.Week
                : CubeTimeDimensionGranularity.Day,
            dateRange: [params.startDate, params.endDate],
          },
        ],
        filters: [
          {
            member: ITeamAnalyticsActivityEnum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityEnum.AppId,
            operator: 'equals',
            values: params.appIds,
          },
        ],
        order: [[ITeamAnalyticsActivityEnum.LocalStartTime, 'asc']],
      };

      if (params?.userIds?.length)
        query.filters.push({
          member: ITeamAnalyticsActivityEnum.UserId,
          operator: 'equals',
          values: params.userIds,
        });

      const latestTeamMetrics = await this.cubeRepository.query(
        config.token,
        query
      );

      return latestTeamMetrics.map((app) =>
        mapLatestFilteredTeamAppMetricsItem(app)
      );
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryLatestTeamAppMetricsError}: ${e}`
      );
    }
  }

  async queryLatestFilteredTeamSubscriptionMetrics(
    params: QueryLatestFilteredTeamSubscriptionMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<FilteredTeamSubscriptionMetricsResult[]> {
    try {
      const query: Query = {
        measures: [
          ITeamAnalyticsActivityEnum.TotalDuration,
          ITeamAnalyticsActivityEnum.Count,
          ITeamAnalyticsActivityEnum.TotalCallDuration,
          ITeamAnalyticsActivityEnum.CallCount,
        ],
        dimensions: [
          ITeamAnalyticsActivityEnum.AppId,
          ITeamAnalyticsActivityEnum.SubscriptionId,
        ],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityEnum.LocalStartTime,
            granularity:
              params.frequency === CubeTimeDimensionGranularity.Week
                ? CubeTimeDimensionGranularity.Week
                : CubeTimeDimensionGranularity.Day,
            dateRange: [params.startDate, params.endDate],
          },
        ],
        filters: [
          {
            member: ITeamAnalyticsActivityEnum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityEnum.AppId,
            operator: 'equals',
            values: params.appIds,
          },
        ],
        order: [[ITeamAnalyticsActivityEnum.LocalStartTime, 'asc']],
      };

      if (params?.userIds?.length)
        query.filters.push({
          member: ITeamAnalyticsActivityEnum.UserId,
          operator: 'equals',
          values: params.userIds,
        });

      const latestTeamMetrics = await this.cubeRepository.query(
        config.token,
        query
      );

      return latestTeamMetrics.map((app) =>
        mapLatestFilteredTeamSubscriptionMetricsItem(app)
      );
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryLatestTeamAppMetricsError}: ${e}`
      );
    }
  }

  async queryUserCount(
    params: QueryUserCountRequestParamsDto,
    config: CubeConfigDto
  ): Promise<UserCountResult[]> {
    try {
      const query: Query = {
        measures: [ITeamAnalyticsActivityEnum.UserCount],
        dimensions: params.includeSubscriptions
          ? [
              ITeamAnalyticsActivityEnum.AppId,
              ITeamAnalyticsActivityEnum.SubscriptionId,
            ]
          : [ITeamAnalyticsActivityEnum.AppId],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityEnum.LocalStartTime,
            granularity:
              params.frequency === CubeTimeDimensionGranularity.Week
                ? CubeTimeDimensionGranularity.Week
                : CubeTimeDimensionGranularity.Day,
            dateRange: [params.startDate, params.endDate],
          },
        ],
        filters: params.userIds?.length
          ? [
              {
                member: ITeamAnalyticsActivityEnum.TeamId,
                operator: 'equals',
                values: [params.teamId],
              },
              {
                member: ITeamAnalyticsActivityEnum.AppId,
                operator: 'equals',
                values: params.appIds,
              },
              {
                member: ITeamAnalyticsActivityEnum.UserId,
                operator: 'equals',
                values: params.userIds,
              },
            ]
          : [
              {
                member: ITeamAnalyticsActivityEnum.TeamId,
                operator: 'equals',
                values: [params.teamId],
              },
              {
                member: ITeamAnalyticsActivityEnum.AppId,
                operator: 'equals',
                values: params.appIds,
              },
            ],
        order: [[ITeamAnalyticsActivityEnum.LocalStartTime, 'asc']],
      };

      const userCounts = await this.cubeRepository.query(config.token, query);
      return userCounts.map((app) => mapUserCount(app));
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryLatestTeamAppMetricsError}: ${e}`
      );
    }
  }

  async queryTeamTotalMetrics(
    params: QueryTeamTotalMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<TeamTotalMetricsResult> {
    try {
      const query: Query = {
        measures: [
          ITeamAnalyticsActivityEnum.TotalDuration,
          ITeamAnalyticsActivityEnum.Count,
          ITeamAnalyticsActivityEnum.UserCount,
          ITeamAnalyticsActivityEnum.DayCount,
        ],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityEnum.LocalStartTime,
            dateRange: [params.startDate, params.endDate],
          },
        ],
        filters: [
          {
            member: ITeamAnalyticsActivityEnum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityEnum.AppId,
            operator: 'equals',
            values: params.appIds,
          },
        ],
        order: [[ITeamAnalyticsActivityEnum.LocalStartTime, 'asc']],
      };

      if (params?.userIds?.length) {
        query.filters.push({
          member: ITeamAnalyticsActivityEnum.UserId,
          operator: 'equals',
          values: params.userIds,
        });
      }

      const [totalMetrics] = await this.cubeRepository.query(
        config.token,
        query
      );
      return mapTeamTotalMetrics(totalMetrics);
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryLatestTeamAppMetricsError}: ${e}`
      );
    }
  }

  async queryAllTimeAverageTeamAppMetrics(
    params: QueryAllTimeAverageTeamAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<AllTimeAverageTeamAppMetricsResult> {
    try {
      const query: Query = {
        measures: [
          ITeamAnalyticsActivityEnum.DayCount,
          ITeamAnalyticsActivityEnum.TotalDuration,
          ITeamAnalyticsActivityEnum.Count,
          ITeamAnalyticsActivityEnum.TotalCallDuration,
          ITeamAnalyticsActivityEnum.CallCount,
          ITeamAnalyticsActivityEnum.UserCount,
          ITeamAnalyticsActivityEnum.WeekCount,
        ],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityEnum.LocalStartTime,
          },
        ],
        order: {},
        filters: [
          {
            member: ITeamAnalyticsActivityEnum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityEnum.AppId,
            operator: 'equals',
            values: [params.appId],
          },
        ],
      };

      if (params?.userIds?.length) {
        query.filters.push({
          member: ITeamAnalyticsActivityEnum.UserId,
          operator: 'equals',
          values: params.userIds,
        });
      }

      const allTimeAverageTeamMetrics = await this.cubeRepository.query(
        config.token,
        query
      );

      const result =
        params.frequency === CubeTimeDimensionGranularity.Week
          ? mapAllTimeAverageTeamMetrics(
              allTimeAverageTeamMetrics,
              CubeTimeDimensionGranularity.Week
            )
          : mapAllTimeAverageTeamMetrics(allTimeAverageTeamMetrics);

      return result;
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryAllTimeAverageTeamAppMetricsError}: ${e}`
      );
    }
  }

  async queryAllTimeWeekdayAverageTeamAppMetrics(
    params: QueryAllTimeWeekdayAverageTeamAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<AllTimeWeekdayAverageTeamAppMetricsResult> {
    try {
      if (params.frequency === CubeTimeDimensionGranularity.Week) {
        throw new Error(
          `Cannot query for weekday average duration with frequency: ${params.frequency}`
        );
      }

      const query: Query = {
        measures: [
          ITeamAnalyticsActivityEnum.DayCount,
          ITeamAnalyticsActivityEnum.UserCount,
          ITeamAnalyticsActivityEnum.TotalDuration,
          ITeamAnalyticsActivityEnum.Count,
        ],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityEnum.LocalStartTime,
          },
        ],
        filters: [
          {
            member: ITeamAnalyticsActivityEnum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityEnum.AppId,
            operator: 'equals',
            values: [params.appId],
          },
        ],
        order: [[ITeamAnalyticsActivityEnum.LocalStartTime, 'desc']],
        segments: [],
      };

      // TODO...deprecated
      if (params.frequency === CubeTimeDimensionGranularity.Day) {
        const dayIndex = new Date(params.endDate).getUTCDay();
        const shiftedDayIndex = (dayIndex + 6) % 7; // shift from Sunday first to Monday first
        const dayOfWeek = Object.values(Weekdays)[shiftedDayIndex];
        query.segments.push(TeamAnalyticsActivitySegments[dayOfWeek]);
      } else {
        query.segments.push(TeamAnalyticsActivitySegments[params.frequency]);
      }

      if (params?.userIds?.length) {
        query.filters.push({
          member: ITeamAnalyticsActivityEnum.UserId,
          operator: 'equals',
          values: params.userIds,
        });
      }

      const weekdayAverages = await this.cubeRepository.query(
        config.token,
        query
      );

      return mapAllTimeWeekdayAverageTeamAppMetrics(weekdayAverages);
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryAllTimeWeekdayAverageAppDurationError}: ${e}`
      );
    }
  }

  async queryAllTimeMaxAppMetric(
    params: QueryAllTimeMaxAppMetricRequestParamsDto,
    config: CubeConfigDto,
    metric: string
  ): Promise<number> {
    try {
      const query: Query = {
        measures: [metric],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityEnum.LocalStartTime,
            granularity:
              params.frequency === CubeTimeDimensionGranularity.Week
                ? CubeTimeDimensionGranularity.Week
                : CubeTimeDimensionGranularity.Day,
          },
        ],
        filters: [
          {
            member: ITeamAnalyticsActivityEnum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityEnum.AppId,
            operator: 'equals',
            values: [params.appId],
          },
        ],
        order: [[metric, 'desc']],
        limit: 1,
        dimensions: [ITeamAnalyticsActivityEnum.AppId],
      };

      if (Object.values<string>(Weekdays).includes(params.frequency)) {
        query.segments = [TeamAnalyticsActivitySegments[params.frequency]];
      }

      if (params?.userIds?.length) {
        query.filters.push({
          member: ITeamAnalyticsActivityEnum.UserId,
          operator: 'equals',
          values: params.userIds,
        });
      }

      const maxDuration = await this.cubeRepository.query(config.token, query);

      if (!maxDuration.length) return null;

      return Number(maxDuration[0][metric]);
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryAllTimeMaxAppMetricError}: ${e}`
      );
    }
  }

  async queryTeamUserTimeSeriesAppMetrics(
    params: QueryTeamUserTimeSeriesAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<TeamUserTimeSeriesResult[]> {
    try {
      const query: Query = {
        measures: [
          ITeamAnalyticsActivityEnum.TotalDuration,
          ITeamAnalyticsActivityEnum.Count,
          ITeamAnalyticsActivityEnum.TotalCallDuration,
          ITeamAnalyticsActivityEnum.CallCount,
          ITeamAnalyticsActivityEnum.DayCount,
        ],
        dimensions: [ITeamAnalyticsActivityEnum.UserId],
        filters: [
          {
            member: ITeamAnalyticsActivityEnum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityEnum.AppId,
            operator: 'equals',
            values: [params.appId],
          },
        ],
        order: [[ITeamAnalyticsActivityEnum.LocalStartTime, 'desc']],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityEnum.LocalStartTime,
            granularity:
              params.frequency === CubeTimeDimensionGranularity.Week
                ? CubeTimeDimensionGranularity.Week
                : CubeTimeDimensionGranularity.Day,
            dateRange: [params.startDate, params.endDate],
          },
        ],
      };

      if (params.userIds) {
        query.filters.push({
          member: ITeamAnalyticsActivityEnum.UserId,
          operator: 'equals',
          values: params.userIds,
        });
      }

      if (Object.values<string>(Weekdays).includes(params.frequency)) {
        query.segments = [TeamAnalyticsActivitySegments[params.frequency]];
      }

      const userTimeSeriesData = await this.cubeRepository.query(
        config.token,
        query
      );

      return mapUserTimeSeriesAppMetrics(userTimeSeriesData);
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryTeamUserTimeSeriesAppMetricsError}: ${e}`
      );
    }
  }

  async queryTeamAverageTimeSeriesAppMetrics(
    params: QueryTeamAverageTimeSeriesAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<TeamTimeSeriesResult[]> {
    try {
      const query: Query = {
        measures: [
          ITeamAnalyticsActivityEnum.DayCount,
          ITeamAnalyticsActivityEnum.UserCount,
          ITeamAnalyticsActivityEnum.Count,
          ITeamAnalyticsActivityEnum.TotalDuration,
          ITeamAnalyticsActivityEnum.CallCount,
          ITeamAnalyticsActivityEnum.TotalCallDuration,
        ],
        filters: [
          {
            member: ITeamAnalyticsActivityEnum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityEnum.AppId,
            operator: 'equals',
            values: [params.appId],
          },
        ],
        order: [[ITeamAnalyticsActivityEnum.LocalStartTime, 'desc']],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityEnum.LocalStartTime,
            granularity:
              params.frequency === CubeTimeDimensionGranularity.Week
                ? CubeTimeDimensionGranularity.Week
                : CubeTimeDimensionGranularity.Day,
            dateRange: [params.startDate, params.endDate],
          },
        ],
      };

      if (Object.values<string>(Weekdays).includes(params.frequency)) {
        query.segments = [TeamAnalyticsActivitySegments[params.frequency]];
      }

      const teamTimeSeriesData = await this.cubeRepository.query(
        config.token,
        query
      );

      return mapTeamAverageTimeSeriesAppMetrics(teamTimeSeriesData);
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryTeamAverageTimeSeriesAppMetricsError}: ${e}`
      );
    }
  }

  async queryTeamTimeSeriesAppMetrics(
    params: QueryTeamTimeSeriesAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<TeamTimeSeriesResult[]> {
    try {
      const query: Query = {
        measures: [
          ITeamAnalyticsActivityEnum.TotalDuration,
          ITeamAnalyticsActivityEnum.Count,
          ITeamAnalyticsActivityEnum.TotalCallDuration,
          ITeamAnalyticsActivityEnum.CallCount,
          ITeamAnalyticsActivityEnum.DayCount,
        ],
        filters: [
          {
            member: ITeamAnalyticsActivityEnum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityEnum.AppId,
            operator: 'equals',
            values: [params.appId],
          },
        ],
        order: [[ITeamAnalyticsActivityEnum.LocalStartTime, 'desc']],
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityEnum.LocalStartTime,
            granularity:
              params.frequency === CubeTimeDimensionGranularity.Week
                ? CubeTimeDimensionGranularity.Week
                : CubeTimeDimensionGranularity.Day,
            dateRange: [params.startDate, params.endDate],
          },
        ],
      };

      if (Object.values<string>(Weekdays).includes(params.frequency)) {
        query.segments = [TeamAnalyticsActivitySegments[params.frequency]];
      }

      const teamTimeSeriesData = await this.cubeRepository.query(
        config.token,
        query
      );

      return mapTeamTimeSeriesAppMetrics(teamTimeSeriesData);
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryTeamTimeSeriesAppMetricsError}: ${e}`
      );
    }
  }

  async queryTeamTimeSeriesAllTimeAverageAppMetrics(
    params: QueryTeamTimeSeriesAllTimeAverageAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<TeamTimeSeriesAllTimeAverageResult> {
    try {
      const query: Query = {
        timeDimensions: [
          {
            dimension: ITeamAnalyticsActivityEnum.LocalStartTime,
          },
        ],
        filters: [
          {
            member: ITeamAnalyticsActivityEnum.TeamId,
            operator: 'equals',
            values: [params.teamId],
          },
          {
            member: ITeamAnalyticsActivityEnum.AppId,
            operator: 'equals',
            values: [params.appId],
          },
        ],
        order: [[ITeamAnalyticsActivityEnum.LocalStartTime, 'desc']],
        measures: [
          ITeamAnalyticsActivityEnum.TotalDuration,
          ITeamAnalyticsActivityEnum.TotalCallDuration,
          ITeamAnalyticsActivityEnum.Count,
          ITeamAnalyticsActivityEnum.CallCount,
          ITeamAnalyticsActivityEnum.DayCount,
          ITeamAnalyticsActivityEnum.WeekCount,
        ],
      };

      if (Object.values<string>(Weekdays).includes(params.frequency)) {
        query.segments = [TeamAnalyticsActivitySegments[params.frequency]];
      }

      const teamAllTimeSeriesMetrics = await this.cubeRepository.query(
        config.token,
        query
      );

      const result =
        params.frequency === CubeTimeDimensionGranularity.Week
          ? mapTeamAllTimeSeriesAppMetrics(
              teamAllTimeSeriesMetrics,
              CubeTimeDimensionGranularity.Week
            )
          : mapTeamAllTimeSeriesAppMetrics(teamAllTimeSeriesMetrics);

      return result;
    } catch (e) {
      throw new Error(
        `${QueryErrorMessages.QueryTeamAllTimeSeriesAppMetricsError}: ${e}`
      );
    }
  }
}
